import { NavLink } from "react-router";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import {
  Camera,
  FileText,
  Inbox,
  LayoutDashboard,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { cn, VButton } from "@/components/ui";
import type { V2Role } from "@/v2/shared/model";
import { useCurrentRole } from "@/v2/auth/useCurrentUser";

type IconType = React.ComponentType<{ className?: string }>;

type NavItem = {
  to: string;
  label: string;
  icon: IconType;
  end?: boolean;
  roles: ReadonlyArray<V2Role>;
};

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    end: true,
    roles: ["manager"],
  },
  { to: "/aanvragen", label: "Aanvragen", icon: Inbox, roles: ["requester", "manager"] },
  { to: "/dossiers", label: "Dossiers", icon: FileText, roles: ["manager"] },
  { to: "/assets", label: "Assets", icon: Camera, roles: ["manager"] },
];

// Ingeklapte staat overleeft een refresh, net als de aangemelde gebruiker.
const sidebarCollapsedAtom = atomWithStorage("pza.v2.sidebarCollapsed", false);

export function V2Sidebar(): React.JSX.Element {
  const role = useCurrentRole();
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  const items = NAV_ITEMS.filter((item) => item.roles.includes(role));

  return (
    <nav
      aria-label="Hoofdnavigatie"
      className={cn(
        "hidden shrink-0 flex-col border-r border-border bg-card py-6 transition-all duration-200 md:flex",
        collapsed ? "w-16 px-3" : "w-60 px-4",
      )}
    >
      <ul className="flex flex-1 flex-col gap-1">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  "group flex h-10 items-center gap-3 rounded-lg text-sm transition-colors",
                  collapsed ? "w-10 justify-center px-0" : "px-3",
                  isActive
                    ? "bg-primary-50 font-semibold text-primary-800"
                    : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      "size-[18px] shrink-0 transition-colors",
                      isActive
                        ? "text-primary-600"
                        : "text-neutral-400 group-hover:text-neutral-500",
                    )}
                    aria-hidden
                  />
                  {collapsed ? null : <span className="truncate">{item.label}</span>}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>

      <div className={cn("border-t border-border pt-3", collapsed && "flex justify-center")}>
        {collapsed ? (
          <VButton
            variant="ghost"
            size="iconSm"
            aria-label="Zijbalk uitklappen"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setCollapsed(false)}
          >
            <PanelLeftOpen className="size-4" aria-hidden />
          </VButton>
        ) : (
          <VButton
            variant="ghost"
            size="sm"
            fullWidth
            className="justify-start text-muted-foreground hover:text-foreground"
            onClick={() => setCollapsed(true)}
          >
            <PanelLeftClose className="size-4" aria-hidden />
            Inklappen
          </VButton>
        )}
      </div>
    </nav>
  );
}
